/**
 * Animation-bezogene Socket Handler
 *
 * Factory-Funktionen für Score-, Card-Animation- und Effekt-Events.
 * Sounds und visuelle Animationen - Toasts wurden entfernt (Event-Log ersetzt sie).
 */

import { useCallback, useRef } from 'react';
import soundService from '../../soundService';
import { getPlayTypeDisplay } from '../../shared/core/constants';
import { MID_GAME, MAGIC_CARDS_TIMING } from '../../config/animationTimings';

/**
 * Erstellt Animation-bezogene Event-Handler
 */
export function useAnimationHandlers(config) {
  // Refs um Timeouts zu tracken - bei mehrfachen Events (Test Mode: vorletzter + letzter gleichzeitig)
  // wird der vorherige Timeout gelöscht, damit Splashes volle 7 Sekunden bleiben
  const playerFinishedTimeoutRef = useRef(null);
  const finishAnimationTimeoutRef = useRef(null);
  const victorySplashTimeoutRef = useRef(null);
  const {
    players,
    activePlayerIndex,
    setFrozenLastPlay,
    setFinishAnimation,
    setFinishedPlayerSplash,
    setVictorySplash,
    setCardAnimations,
    addFlyingCards,
    addOtherPlayerFlyingCards,
    addOtherPlayerPlayingCards,
    flashMessage,
    setTriggeredJokers,
    setActiveMagicCard,
  } = config;

  /**
   * Handler für scoreUpdate Event
   * Sounds und Animationen für Stichgewinn/Finish
   */
  const handleScoreUpdate = useCallback((data) => {
    const { playerIndex, type, points: pts, playType, jokerEffects, winningCards } = data;
    const playerName = players[playerIndex]?.nickname || `Player ${playerIndex + 1}`;
    const isMe = playerIndex === activePlayerIndex;

    if (type === 'trickWin') {
      soundService.play(isMe ? 'trick_win' : 'trick_lose');

      // Freeze winning cards briefly
      if (winningCards && winningCards.length > 0) {
        setFrozenLastPlay([...winningCards]);
        setTimeout(() => setFrozenLastPlay(null), 1500);
      }

      // Play joker sound and trigger animation if jokers triggered
      if (jokerEffects && jokerEffects.length > 0) {
        soundService.play('joker_activate');
        // Play bonus sound if jokers gave points/mult
        const hasBonus = jokerEffects.some(e => e.points > 0 || e.mult > 0 || e.coins > 0);
        if (hasBonus) {
          setTimeout(() => soundService.play('joker_bonus'), 200);
        }

        // Trigger visual animation for joker cards
        if (setTriggeredJokers) {
          // Server sends sourceId (from messageFactory.js)
          const jokerIds = jokerEffects.map(e => e.sourceId || e.jokerId || e.id).filter(Boolean);
          setTriggeredJokers(jokerIds);
          // Clear after animation duration
          setTimeout(() => setTriggeredJokers([]), 1500);
        }
      }

      // Status message with trick name
      const playTypeInfo = getPlayTypeDisplay(playType);
      const trickDisplayName = playTypeInfo.name;
      if (flashMessage) {
        flashMessage(
          isMe ? `Du gewinnst mit ${trickDisplayName}!` : `${playerName} gewinnt mit ${trickDisplayName}`,
          'effect',
          3000
        );
      }

    } else if (type === 'jokerBonus') {
      // Joker bonus from card received (e.g., Six Seven joker)
      if (jokerEffects && jokerEffects.length > 0) {
        soundService.play('joker_activate');
        setTimeout(() => soundService.play('joker_bonus'), 200);

        // Trigger visual animation for joker cards
        if (setTriggeredJokers) {
          const jokerIds = jokerEffects.map(e => e.sourceId || e.jokerId || e.id).filter(Boolean);
          setTriggeredJokers(jokerIds);
          setTimeout(() => setTriggeredJokers([]), 1500);
        }
      }

    } else if (type === 'finish') {
      const totalPlayers = players.length;
      const isLastPlace = data.position === totalPlayers;
      const positionEmojis = ['🥇', '🥈', '🥉'];

      // Show animation to ALL players (not just the finishing player)
      const shouldShowAnimation = true;

      // Play finish sound
      if (data.position === 1) {
        soundService.play(shouldShowAnimation ? 'game_win' : 'player_finished');
      } else if (isLastPlace && shouldShowAnimation) {
        soundService.play('game_lose');
      } else {
        soundService.play('player_finished');
      }

      // Show finish animation for own finish (or test mode)
      if (shouldShowAnimation) {
        // Vorherigen Timeout löschen falls vorhanden (wichtig für Test Mode)
        if (finishAnimationTimeoutRef.current) {
          clearTimeout(finishAnimationTimeoutRef.current);
        }

        setFinishAnimation({
          playerName: playerName,
          position: data.position,
          totalPlayers,
          positionEmoji: isLastPlace ? '💩' : (positionEmojis[data.position - 1] || `#${data.position}`),
          points: pts,
          coins: data.coins || 0,
          isMe: isMe,  // True only for the finishing player (for text differentiation)
          isLastPlace
        });

        // Use same timing as mid-game finish splash
        finishAnimationTimeoutRef.current = setTimeout(() => {
          setFinishAnimation(null);
          finishAnimationTimeoutRef.current = null;
        }, MID_GAME.finishSplash);
      }

      // Status message (only for the finishing player, splash already shows for all)
      if (flashMessage && isMe) {
        flashMessage(isLastPlace ? 'Du bist der Arsch!' : `Platz ${data.position}!`, 'effect', 3000);
      }
    }
  }, [players, activePlayerIndex, setFrozenLastPlay, setFinishAnimation, flashMessage, setTriggeredJokers]);

  /**
   * Handler für playerFinished Event (Mid-Game Finish)
   * Zeigt kurzen Splash wenn ein Spieler fertig wird, aber Spiel weiterläuft.
   * Timing konfigurierbar in animationTimings.js (midGame.finishSplash)
   */
  const handlePlayerFinished = useCallback((data) => {
    // Vorherigen Timeout löschen falls vorhanden (wichtig für Test Mode)
    if (playerFinishedTimeoutRef.current) {
      clearTimeout(playerFinishedTimeoutRef.current);
    }

    setFinishedPlayerSplash({
      playerIndex: data.playerIndex,
      playerName: data.playerName,
      position: data.position,
      totalPlayers: data.totalPlayers,
      isMe: data.playerIndex === activePlayerIndex
    });

    // Auto-dismiss nach konfigurierbarer Zeit
    playerFinishedTimeoutRef.current = setTimeout(() => {
      setFinishedPlayerSplash(null);
      playerFinishedTimeoutRef.current = null;
    }, MID_GAME.finishSplash);
  }, [activePlayerIndex, setFinishedPlayerSplash]);

  /**
   * Handler für cardAnimation Event
   * Sounds und fliegende Karten-Animationen
   */
  const handleCardAnimation = useCallback((data) => {
    const { type, cards, cardCount, reason, icon, text, playerIndex, playSound, jokerTriggered, fartCount, seal } = data;
    const isMe = playerIndex === activePlayerIndex;

    if (type === 'added') {
      soundService.play('card_draw');

      // Flying card animation
      if (isMe) {
        if (cards && cards.length > 0 && addFlyingCards) {
          addFlyingCards(cards, 'added', { seal });
        }
      } else {
        const count = cardCount || cards?.length || 1;
        if (addOtherPlayerFlyingCards && playerIndex !== undefined) {
          addOtherPlayerFlyingCards(count, playerIndex);
        }
      }
      return;
    } else if (type === 'removed') {
      // Play different sound for glass break vs normal discard
      if (reason === 'glassBreak') {
        soundService.play('glass_break');
      } else {
        soundService.play('card_pass');
      }

      // Flying card animation (hand → down + fade) - only for the affected player
      if (isMe && cards && cards.length > 0 && addFlyingCards) {
        addFlyingCards(cards, 'removed');
      }
      return;
    } else if (type === 'toDeck') {
      soundService.play('card_pass');

      // Flying card animation (hand → deck) for swap - only for the affected player
      if (isMe && cards && cards.length > 0 && addFlyingCards) {
        addFlyingCards(cards, 'toDeck');
      }
      return;
    } else if (type === 'played') {
      // Play sound from server (may be joker sound like fart, or default card sound)
      // Sound plays for ALL players
      if (playSound === 'joker_fart' && fartCount > 0) {
        // Kleiner Furz: Play random fart sounds for each 3 played
        for (let i = 0; i < fartCount; i++) {
          const randomFart = `joker_fart_${Math.floor(Math.random() * 5) + 1}`;
          // Slight delay between multiple farts for comedic effect
          setTimeout(() => soundService.play(randomFart), i * 100);
        }
      } else if (playSound) {
        soundService.play(playSound);
      }

      // Trigger joker animation if a joker was triggered (e.g., Kleiner Furz)
      if (jokerTriggered && setTriggeredJokers) {
        setTriggeredJokers([jokerTriggered]);
        setTimeout(() => setTriggeredJokers([]), 1500);
      }

      // Card play animation - only animate for OTHER players
      if (!isMe && cards && cards.length > 0 && addOtherPlayerPlayingCards && playerIndex !== undefined) {
        addOtherPlayerPlayingCards(cards, playerIndex);
      }
      return;
    } else if (type === 'jokersAdded') {
      // Lückenfüller seal: Jokers added to player's inventory
      // Trigger animation for the new jokers so player notices them
      const { jokerIds } = data;
      if (isMe && jokerIds && jokerIds.length > 0 && setTriggeredJokers) {
        soundService.play('joker_activate');
        setTimeout(() => soundService.play('joker_bonus'), 200);
        setTriggeredJokers(jokerIds);
        setTimeout(() => setTriggeredJokers([]), 1500);
      }
      return;
    } else if (type === 'coinChange') {
      // Play specific sound based on seal type/reason
      const reasonLower = reason?.toLowerCase() || '';

      // Passive Einnahmen: Only play soft coin sound, no trigger sound
      if (reasonLower.includes('passiv') || reasonLower.includes('passive einnahmen')) {
        soundService.play('seal_coins');
      } else {
        // Other coin changes: Play seal_trigger + specific sound
        soundService.play('seal_trigger');
        if (reasonLower.includes('steal') || reasonLower.includes('dieb')) {
          soundService.play('seal_steal');
        } else if (reasonLower.includes('gift') || reasonLower.includes('poison')) {
          soundService.play('seal_poison');
        } else if (reasonLower.includes('fluch') || reasonLower.includes('curse')) {
          soundService.play('seal_curse');
        } else if (reasonLower.includes('heil') || reasonLower.includes('heal')) {
          soundService.play('seal_heal');
        } else {
          soundService.play('seal_coins');
        }
      }
    } else if (type === 'multChange') {
      soundService.play('seal_trigger');
      soundService.play('seal_coins');
    } else if (type === 'extraPlay') {
      soundService.play('seal_extra_play');
    } else if (type === 'sealEffect') {
      // Handle specific seal effect sounds
      const reasonLower = reason?.toLowerCase() || '';
      if (reasonLower.includes('frost') || reasonLower.includes('freeze') || reasonLower.includes('eis')) {
        soundService.play('seal_freeze');
      } else if (reasonLower.includes('flamm') || reasonLower.includes('feuer') || reasonLower.includes('burn')) {
        soundService.play('seal_burn');
      } else if (reasonLower.includes('spiegel') || reasonLower.includes('mirror')) {
        soundService.play('seal_mirror');
      } else if (reasonLower.includes('schatten') || reasonLower.includes('shadow')) {
        soundService.play('seal_shadow');
      } else if (reasonLower.includes('schild') || reasonLower.includes('shield')) {
        soundService.play('seal_shield');
      } else {
        soundService.play('seal_trigger');
      }
    } else if (type === 'milkGlassSuccess') {
      // Milchglas seal: Card survived (70% chance) - sound only, no badge
      soundService.play('glass_success');
      return;
    }

    // Show badge animation for other types
    const animId = Date.now() + Math.random();
    setCardAnimations([{
      id: animId,
      type,
      cards,
      reason,
      icon,
      text
    }]);

    setTimeout(() => {
      setCardAnimations(prev => prev.filter(a => a.id !== animId));
    }, 2000);
  }, [activePlayerIndex, addFlyingCards, addOtherPlayerFlyingCards, addOtherPlayerPlayingCards, setCardAnimations, setTriggeredJokers]);

  /**
   * Handler für victorySplash Event
   * Zeigt "Spieler X gewinnt!" Splash bevor Victory-Slides starten.
   */
  const handleVictorySplash = useCallback((data) => {
    const { winnerIndex, winnerName, points, targetPoints } = data;
    const isMe = winnerIndex === activePlayerIndex;

    // Clear previous timeout if any
    if (victorySplashTimeoutRef.current) {
      clearTimeout(victorySplashTimeoutRef.current);
    }

    soundService.play('game_win');

    setVictorySplash({
      winnerIndex,
      winnerName,
      points,
      targetPoints,
      isMe,
    });

    // Auto-dismiss after configured duration
    victorySplashTimeoutRef.current = setTimeout(() => {
      setVictorySplash(null);
      victorySplashTimeoutRef.current = null;
    }, MID_GAME.victorySplash);
  }, [activePlayerIndex, setVictorySplash]);

  /**
   * Handler für magicCardDrawn Event
   * Zeigt Zauberkarten-Animation und triggert dann Minispiel
   */
  const handleMagicCardDrawn = useCallback((data) => {
    const { playerIndex, playerName, magicCard } = data;

    if (!magicCard || !setActiveMagicCard) return;

    // Play same sound as normal card draw
    soundService.play('card_draw');

    // Set active magic card to trigger animation
    setActiveMagicCard({
      ...magicCard,
      playerIndex,
      playerName,
    });

    // Auto-dismiss after animation completes (server will start minigame)
    setTimeout(() => {
      setActiveMagicCard(null);
    }, MAGIC_CARDS_TIMING.totalDuration);

    // Flash message
    if (flashMessage) {
      const isMe = playerIndex === activePlayerIndex;
      flashMessage(
        isMe ? `Du ziehst eine Zauberkarte: ${magicCard.name}!` : `${playerName} zieht eine Zauberkarte!`,
        'effect',
        3000
      );
    }
  }, [activePlayerIndex, setActiveMagicCard, flashMessage]);

  return {
    handleScoreUpdate,
    handlePlayerFinished,
    handleCardAnimation,
    handleMagicCardDrawn,
    handleVictorySplash,
  };
}

export default useAnimationHandlers;
